import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { RefreshCw, Search, X, Eye, Download, Save, Printer, CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import ReactDOM from 'react-dom';
import MainService from '../../services/MainService';
import { getApiBaseUrl } from '../../services/appSettings';
import { useLanguage } from '../../contexts/LanguageContext';
import { getPageHeading } from '../../locales/navigation';
import SortFilterTable, { useClientListGrid } from '../ui/SortFilterTable';
import { GridText, StatusPill } from '../ui/gridCell';
import {
  PAYMENT_CLIENT_COLUMNS,
  PAYMENT_ORDER_MODAL_COLUMNS,
  getPaymentClientCellValue,
  getPaymentOrderCellValue,
} from '../ui/tableColumnConfigs';

const PAGE_SIZE = 100;

const CreatePayment = () => {
  const { language } = useLanguage();
  const [allClients, setAllClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadAllMode, setLoadAllMode] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [showOrdersModal, setShowOrdersModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [voucherSearch, setVoucherSearch] = useState('');
  const [companyProfile, setCompanyProfile] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [emirates, setEmirates] = useState([]);
  const [areas, setAreas] = useState([]);
  const [clientPayment, setClientPayment] = useState({
    RefNo: '',
    EmployeeId: '',
    Date: new Date().toISOString().split('T')[0],
    ClientMoney: 0
  });
  const [filters, setFilters] = useState({
    ClientId: '',
    EmirateId: '',
    AreaId: '',
    EmployeeId: ''
  });
  const [selectAll, setSelectAll] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveAndPrint, setSaveAndPrint] = useState(false);
  const [saveAsIndividualPayments, setSaveAsIndividualPayments] = useState(false);

  // Toast notification effect
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Load company profile
  useEffect(() => {
    const loadCompanyProfile = async () => {
      try {
        const response = await MainService.GetCompanyProfile();
        setCompanyProfile(response);
      } catch (err) {
        console.error('Error loading company profile:', err);
      }
    };
    loadCompanyProfile();
  }, []);

  // Load dropdowns
  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        const [employeesResp, emiratesResp, areasResp] = await Promise.all([
          MainService.EmployeesInDropDown(),
          MainService.EmiratesInDropDown(),
          MainService.AreasInDropDown()
        ]);
        setEmployees(employeesResp || []);
        setEmirates(emiratesResp || []);
        setAreas(areasResp || []);
      } catch (err) {
        console.error('Error loading dropdowns:', err);
      }
    };
    loadDropdowns();
  }, []);

  // Load next payment number
  useEffect(() => {
    const loadNextPaymentNo = async () => {
      try {
        const response = await MainService.GetNextClientPaymentNo();
        if (response) {
          setClientPayment(prev => ({ ...prev, RefNo: response }));
        }
      } catch (err) {
        console.error('Error loading next payment number:', err);
      }
    };
    loadNextPaymentNo();
  }, []);

  const getFilterRequestData = () => ({
    EmirateId: filters.EmirateId || 0,
    AreaId: filters.AreaId || 0,
    ClientId: filters.ClientId || 0,
    EmployeeId: filters.EmployeeId || 0,
  });

  // Load one page of clients (default first 100)
  const loadClients = async (page = 1) => {
    try {
      setLoading(true);
      setError('');
      setLoadAllMode(false);
      setSelectAll(false);
      setClientPayment((prev) => ({ ...prev, ClientMoney: 0 }));

      const response = await MainService.ClientsInClientPaymentList({
        ...getFilterRequestData(),
        Skip: (page - 1) * PAGE_SIZE,
        Take: PAGE_SIZE,
      });

      const data = response?.Data || [];
      setAllClients(data);
      setTotalCount(response?.Total || data.length);
      setCurrentPage(page);
    } catch (err) {
      console.error('Error loading clients:', err);
      setError('Failed to load clients');
      setAllClients([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Load all clients in batches of 100
  const loadAllClients = async () => {
    try {
      setLoading(true);
      setError('');
      setSelectAll(false);
      setClientPayment((prev) => ({ ...prev, ClientMoney: 0 }));

      let allClientsData = [];
      let skip = 0;
      let total = 0;
      let hasMore = true;
      const baseRequestData = getFilterRequestData();

      while (hasMore) {
        const response = await MainService.ClientsInClientPaymentList({
          ...baseRequestData,
          Skip: skip,
          Take: PAGE_SIZE,
        });

        const batch = response?.Data || [];
        total = response?.Total || 0;
        allClientsData = [...allClientsData, ...batch];
        skip += batch.length;

        if (batch.length === 0 || allClientsData.length >= total) {
          hasMore = false;
        }
      }

      setAllClients(allClientsData);
      setTotalCount(total || allClientsData.length);
      setCurrentPage(1);
      setLoadAllMode(true);
    } catch (err) {
      console.error('Error loading all clients:', err);
      setError('Failed to load all clients');
      setAllClients([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClients(1);
  }, []);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  // Front-end search filtering
  const filteredClients = useMemo(() => {
    if (!searchKeyword.trim()) {
      return allClients;
    }

    const keyword = searchKeyword.toLowerCase();
    return allClients.filter(client => 
      (client.Name || '').toLowerCase().includes(keyword) ||
      String(client.ClientNo ?? '').toLowerCase().includes(keyword) ||
      (client.MobileNo1 || '').toLowerCase().includes(keyword) ||
      (client.LocationName || '').toLowerCase().includes(keyword) ||
      (client.EmirateName || '').toLowerCase().includes(keyword) ||
      (client.AreaName || '').toLowerCase().includes(keyword)
    );
  }, [allClients, searchKeyword]);

  // Filter clients
  const handleFilterClients = () => {
    if (filters.EmployeeId) {
      setClientPayment(prev => ({ ...prev, EmployeeId: filters.EmployeeId }));
    }
    loadClients(1);
  };

  // Refresh clients
  const handleRefresh = () => {
    setFilters({
      ClientId: '',
      EmirateId: '',
      AreaId: '',
      EmployeeId: ''
    });
    setSearchKeyword('');
    setSelectAll(false);
    setClientPayment(prev => ({
      ...prev,
      ClientMoney: 0,
      EmployeeId: ''
    }));
    loadClients(1);
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages || page === currentPage || loading || loadAllMode) return;
    loadClients(page);
  };

  // Toggle select all (within currently filtered/loaded rows)
  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);

    const filteredIds = new Set(filteredClients.map((c) => c.Id));
    const updatedClients = allClients.map((client) => {
      if (!filteredIds.has(client.Id)) return client;
      if (newSelectAll) {
        const updatedOrders = (client.Orders || []).map((order) => ({ ...order, IsChecked: true }));
        const clientTotal = updatedOrders.reduce((sum, order) => sum + (order.ClientAmount || 0), 0);
        return {
          ...client,
          checked: true,
          Orders: updatedOrders,
          TotalPayingMoney: clientTotal,
        };
      }
      const updatedOrders = (client.Orders || []).map((order) => ({ ...order, IsChecked: false }));
      return {
        ...client,
        checked: false,
        Orders: updatedOrders,
        TotalPayingMoney: 0,
      };
    });

    const totalMoney = updatedClients.reduce((sum, client) => sum + (client.TotalPayingMoney || 0), 0);
    setAllClients(updatedClients);
    setClientPayment((prev) => ({ ...prev, ClientMoney: totalMoney }));
  };

  // Toggle client selection
  const handleToggleClient = (clientId) => {
    const updatedClients = allClients.map(client => {
      if (client.Id === clientId) {
        const newChecked = !client.checked;
        let clientTotal = 0;
        
        if (newChecked) {
          // Select all orders for this client
          const updatedOrders = client.Orders.map(order => {
            const checkedOrder = { ...order, IsChecked: true };
            clientTotal += order.ClientAmount || 0;
            return checkedOrder;
          });
          return {
            ...client,
            checked: true,
            Orders: updatedOrders,
            TotalPayingMoney: clientTotal
          };
        } else {
          // Deselect all orders
          const updatedOrders = client.Orders.map(order => ({ ...order, IsChecked: false }));
          return {
            ...client,
            checked: false,
            Orders: updatedOrders,
            TotalPayingMoney: 0
          };
        }
      }
      return client;
    });

    const totalMoney = updatedClients.reduce((sum, client) => sum + (client.TotalPayingMoney || 0), 0);
    setAllClients(updatedClients);
    setClientPayment(prev => ({ ...prev, ClientMoney: totalMoney }));
  };

  // Toggle order selection in modal
  const handleToggleOrder = (orderId) => {
    if (!selectedClient) return;

    const updatedClients = allClients.map(client => {
      if (client.Id === selectedClient.Id) {
        const updatedOrders = client.Orders.map(order => {
          if (order.Id === orderId) {
            const newChecked = !order.IsChecked;
            return { ...order, IsChecked: newChecked };
          }
          return order;
        });

        const clientTotal = updatedOrders
          .filter(order => order.IsChecked)
          .reduce((sum, order) => sum + (order.ClientAmount || 0), 0);

        return {
          ...client,
          Orders: updatedOrders,
          TotalPayingMoney: clientTotal
        };
      }
      return client;
    });

    const totalMoney = updatedClients.reduce((sum, client) => sum + (client.TotalPayingMoney || 0), 0);
    setAllClients(updatedClients);
    setClientPayment(prev => ({ ...prev, ClientMoney: totalMoney }));

    // Update selected client for modal display
    const updatedClient = updatedClients.find(c => c.Id === selectedClient.Id);
    if (updatedClient) {
      setSelectedClient(updatedClient);
    }
  };

  // View orders
  const handleViewOrders = (client) => {
    setSelectedClient(client);
    setShowOrdersModal(true);
  };

  // Update payment mode
  const handlePaymentModeChange = (clientId, payMode) => {
    const updatedClients = allClients.map(client => {
      if (client.Id === clientId) {
        return { ...client, PayMode: payMode };
      }
      return client;
    });
    setAllClients(updatedClients);
  };

  // Open save modal
  const handleOpenSaveModal = (shouldPrint = false) => {
    // Amount may be 0, positive, or negative — only require clients selected
    const selectedClients = allClients.filter(
      (c) => c.checked && c.Orders.some((order) => order.IsChecked)
    );
    
    if (selectedClients.length === 0) {
      setError('Select at least one client');
      return;
    }

    setSaveAndPrint(shouldPrint);
    setShowSaveModal(true);
  };

  // Save client payment
  const handleSavePayment = async () => {
    if (!clientPayment.EmployeeId) {
      setError('Select Employee');
      return;
    }

    try {
      setLoading(true);
      // Amount may be 0, positive, or negative — only require clients selected
      const selectedClients = allClients.filter(
        (c) => c.checked && c.Orders.some((order) => order.IsChecked)
      );
      
      if (selectedClients.length === 0) {
        setError('Select at least one client');
        return;
      }

      const currentDate = new Date(clientPayment.Date);
      const paymentData = {
        ...clientPayment,
        Month: currentDate.getMonth() + 1,
        Day: currentDate.getDate(),
        Year: currentDate.getFullYear(),
        ClientPaymentClients: selectedClients.map(client => ({
          ClientMoney: client.TotalPayingMoney,
          PayMode: client.PayMode || 'CASH',
          TotalOrders: client.Orders.filter(o => o.IsChecked).length,
          ClientId: client.Id,
          ClientPaymentClientOrders: client.Orders
            .filter(order => order.IsChecked)
            .map(order => ({
              ClientMoney: order.ClientAmount,
              OrderId: order.Id
            }))
        }))
      };

      const response = saveAsIndividualPayments
        ? await MainService.SaveSeperateClientPayment(paymentData)
        : await MainService.SaveClientPayment(paymentData);

      if (response && response.Status === 'Success') {
        setSuccess(
          saveAsIndividualPayments
            ? 'Individual payments saved successfully'
            : 'Payment saved successfully'
        );
        setShowSaveModal(false);
        setSaveAsIndividualPayments(false);
        if (saveAndPrint) {
          const printId =
            response.ClientPaymentId ||
            (Array.isArray(response.PaymentIds) && response.PaymentIds.length
              ? response.PaymentIds[0]
              : null);
          if (printId) {
            await handlePrintPayment(printId);
          }
          // Print remaining individual payments if any
          if (saveAsIndividualPayments && Array.isArray(response.PaymentIds)) {
            for (let i = 1; i < response.PaymentIds.length; i += 1) {
              await handlePrintPayment(response.PaymentIds[i]);
            }
          }
        }
        handleRefresh();
      } else {
        setError(response?.Status || 'Failed to save payment');
      }
    } catch (err) {
      console.error('Error saving payment:', err);
      setError('Failed to save payment');
    } finally {
      setLoading(false);
    }
  };

  // Print payment
  const handlePrintPayment = async (paymentId) => {
    try {
      const response = await MainService.PrintClientPayment(paymentId);
      if (response) {
        window.open(`${getApiBaseUrl()}ReportFiles/${response}`, '_blank');
      }
    } catch (err) {
      console.error('Error printing payment:', err);
      setError('Failed to print payment');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '0.00';
    return parseFloat(amount).toFixed(2);
  };

  // Filter orders in modal by voucher search
  const filteredOrders = useMemo(() => {
    if (!selectedClient) return [];
    if (!voucherSearch.trim()) return selectedClient.Orders || [];
    
    const keyword = voucherSearch.toLowerCase();
    return (selectedClient.Orders || []).filter(order =>
      (order.VoucherNo || '').toLowerCase().includes(keyword)
    );
  }, [selectedClient, voucherSearch]);

  // Calculate selected count and amount for modal
  const selectedOrderStats = useMemo(() => {
    if (!selectedClient) return { count: 0, amount: 0 };
    const selectedOrders = (selectedClient.Orders || []).filter(o => o.IsChecked);
    return {
      count: selectedOrders.length,
      amount: selectedOrders.reduce((sum, o) => sum + (o.ClientAmount || 0), 0)
    };
  }, [selectedClient]);

  const clientColumns = useMemo(() => PAYMENT_CLIENT_COLUMNS, []);
  const orderModalColumns = useMemo(() => PAYMENT_ORDER_MODAL_COLUMNS, []);

  const getClientCell = useCallback((client, field) => getPaymentClientCellValue(client, field), []);
  const getOrderCell = useCallback(
    (order, field) => getPaymentOrderCellValue(order, field, formatCurrency),
    []
  );

  const clientGrid = useClientListGrid({
    columns: clientColumns,
    rows: filteredClients,
    getCellValue: getClientCell,
  });

  const orderGrid = useClientListGrid({
    columns: orderModalColumns,
    rows: filteredOrders,
    getCellValue: getOrderCell,
  });

  const renderClientCell = useCallback((client, column) => {
    switch (column.field) {
      case 'payMode':
        return (
          <select
            value={client.PayMode || 'CASH'}
            onChange={(e) => handlePaymentModeChange(client.Id, e.target.value)}
            className="w-full min-w-0 rounded-lg border border-gray-300 px-2 py-1 text-sm focus:border-transparent focus:ring-2 focus:ring-primary"
          >
            <option value="CASH">CASH</option>
            <option value="BANK">BANK</option>
          </select>
        );
      case 'totalAmount':
        return <GridText value={`AED ${formatCurrency(client.TotalClientMoney)}`} />;
      case 'totalPaying':
        return <GridText value={`AED ${formatCurrency(client.TotalPayingMoney || 0)}`} className="font-semibold text-gray-900" />;
      case 'actions':
        return (
          <button
            type="button"
            onClick={() => handleViewOrders(client)}
            className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm text-white transition-colors hover:bg-blue-700"
          >
            Orders
          </button>
        );
      default:
        return <GridText value={getPaymentClientCellValue(client, column.field)} />;
    }
  }, []);

  const renderOrderCell = useCallback((order, column) => {
    if (column.field === 'status') {
      return <StatusPill status={order.Status} />;
    }
    if (['productCharge', 'serviceCharge', 'vat', 'clientMoney'].includes(column.field)) {
      const raw = getPaymentOrderCellValue(order, column.field, formatCurrency);
      return <GridText value={raw === '-' ? raw : `AED ${raw}`} />;
    }
    return <GridText value={getPaymentOrderCellValue(order, column.field, formatCurrency)} />;
  }, []);

  return (
    <div className="space-y-6">
      {/* Toast Messages */}
      {error && (
        <div className="fixed top-24 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-[100] min-w-[300px]">
          <X className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-24 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-[100] min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              <span>{getPageHeading(language, 'createPayment')}</span>
              <span className="inline-block ms-1" dir="ltr">
                {' '}
                — {clientPayment.RefNo || getPageHeading(language, 'newRef')}
              </span>
            </h1>
            <p className="text-sm text-gray-500 mt-1">Create client payment from approved orders</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleOpenSaveModal(false)}
              disabled={loading}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              Save
            </button>
            <button
              onClick={() => handleOpenSaveModal(true)}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Printer className="w-4 h-4" />
              Save & Print
            </button>
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Refresh"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="grid grid-cols-12 gap-4 mb-4">
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Emirate</label>
            <select
              value={filters.EmirateId}
              onChange={(e) => setFilters(prev => ({ ...prev, EmirateId: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">All</option>
              {emirates.map(emirate => (
                <option key={emirate.Id} value={emirate.Id}>{emirate.Name}</option>
              ))}
            </select>
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Area</label>
            <select
              value={filters.AreaId}
              onChange={(e) => setFilters(prev => ({ ...prev, AreaId: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">All</option>
              {areas.map(area => (
                <option key={area.Id} value={area.Id}>{area.Name}</option>
              ))}
            </select>
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Responsible By</label>
            <select
              value={filters.EmployeeId}
              onChange={(e) => setFilters(prev => ({ ...prev, EmployeeId: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">All</option>
              {employees.map(employee => (
                <option key={employee.Id} value={employee.Id}>{employee.Name}</option>
              ))}
            </select>
          </div>
          <div className="col-span-2 flex items-end gap-2">
            <button
              onClick={handleFilterClients}
              disabled={loading}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50"
            >
              Filter
            </button>
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              Clear
            </button>
          </div>
          <div className="col-span-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                placeholder="Search clients..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              {searchKeyword && (
                <button
                  onClick={() => setSearchKeyword('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <SortFilterTable
          columns={clientColumns}
          grid={clientGrid}
          rows={filteredClients}
          getCellValue={getClientCell}
          renderCell={renderClientCell}
          loading={loading}
          loadingText="Loading clients..."
          emptyText="No clients found"
          minWidth="1500px"
          maxHeight={600}
          showPagination
          leadingHeader={
            <th className="w-[3%] px-4 py-2 text-left">
              <input type="checkbox" checked={selectAll} onChange={handleSelectAll} className="h-4 w-4" />
            </th>
          }
          renderLeadingCell={(client) => (
            <td className="px-4 py-2">
              <input
                type="checkbox"
                checked={client.checked || false}
                onChange={() => handleToggleClient(client.Id)}
                className="h-4 w-4"
              />
            </td>
          )}
        />

        {/* Pagination + Load All */}
        {totalCount > 0 && (
          <div className="px-6 py-3 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-sm text-gray-600">
              {loadAllMode ? (
                <>Showing all <span className="font-semibold">{allClients.length}</span> of <span className="font-semibold">{totalCount}</span> clients</>
              ) : (
                <>
                  Showing{' '}
                  <span className="font-semibold">{(currentPage - 1) * PAGE_SIZE + (filteredClients.length ? 1 : 0)}</span>
                  {' - '}
                  <span className="font-semibold">{(currentPage - 1) * PAGE_SIZE + filteredClients.length}</span>
                  {' of '}
                  <span className="font-semibold">{totalCount}</span> clients
                </>
              )}
            </p>
            <div className="flex items-center gap-2 flex-wrap justify-center">
              {!loadAllMode && totalCount > PAGE_SIZE && (
                <>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={loading || currentPage <= 1}
                    className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 text-sm"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Prev
                  </button>
                  <span className="text-sm text-gray-700 px-2">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={loading || currentPage >= totalPages}
                    className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 text-sm"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </>
              )}
              {!loadAllMode && totalCount > allClients.length && (
                <button
                  onClick={loadAllClients}
                  disabled={loading}
                  className="px-4 py-1.5 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 text-sm font-medium"
                >
                  Load All ({totalCount})
                </button>
              )}
              {loadAllMode && totalCount > PAGE_SIZE && (
                <button
                  onClick={() => loadClients(1)}
                  disabled={loading}
                  className="px-4 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 text-sm font-medium"
                >
                  Use Pagination
                </button>
              )}
            </div>
          </div>
        )}

        {/* Total Summary */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <div className="flex items-center justify-center">
            <p className="text-lg font-semibold text-gray-900">
              Total Client Money: <span className="text-primary">AED {formatCurrency(clientPayment.ClientMoney)}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Save Payment Modal */}
      {showSaveModal && ReactDOM.createPortal(
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-900">Save Payment</h3>
              <button
                onClick={() => {
                  setShowSaveModal(false);
                  setSaveAndPrint(false);
                  setSaveAsIndividualPayments(false);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Employee</label>
                <select
                  value={clientPayment.EmployeeId}
                  onChange={(e) => setClientPayment(prev => ({ ...prev, EmployeeId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="">Select Employee</option>
                  {employees.map(employee => (
                    <option key={employee.Id} value={employee.Id}>{employee.Name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                <input
                  type="date"
                  value={clientPayment.Date}
                  onChange={(e) => setClientPayment(prev => ({ ...prev, Date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <label className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 cursor-pointer hover:bg-gray-100">
                <input
                  type="checkbox"
                  checked={saveAsIndividualPayments}
                  onChange={(e) => setSaveAsIndividualPayments(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span>
                  <span className="block text-sm font-medium text-gray-900">Save as Individual Payments</span>
                  <span className="block text-xs text-gray-500 mt-0.5">
                    Creates a separate payment voucher for each selected client
                  </span>
                </span>
              </label>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowSaveModal(false);
                  setSaveAndPrint(false);
                  setSaveAsIndividualPayments(false);
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePayment}
                disabled={loading || !clientPayment.EmployeeId}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {saveAndPrint ? (
                  <>
                    <Printer className="w-4 h-4" />
                    Save & Print
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save
                  </>
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Orders Modal */}
      {showOrdersModal && selectedClient && ReactDOM.createPortal(
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-900">Orders - {selectedClient.Name}</h3>
              <button
                onClick={() => {
                  setShowOrdersModal(false);
                  setSelectedClient(null);
                  setVoucherSearch('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Voucher Search */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Voucher Number</label>
              <input
                type="text"
                value={voucherSearch}
                onChange={(e) => setVoucherSearch(e.target.value)}
                placeholder="Search by voucher number..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            {/* Orders Table */}
            <SortFilterTable
              columns={orderModalColumns}
              grid={orderGrid}
              rows={filteredOrders}
              getCellValue={getOrderCell}
              renderCell={renderOrderCell}
              emptyText="No orders found"
              minWidth="1600px"
              maxHeight={500}
              showPagination
              leadingHeader={
                !companyProfile?.IsBlockByOrderSelection ? (
                  <th className="w-[3%] px-4 py-2 text-left text-xs font-medium uppercase text-gray-700">Select</th>
                ) : null
              }
              renderLeadingCell={
                !companyProfile?.IsBlockByOrderSelection
                  ? (order) => (
                      <td className="px-4 py-2">
                        <input
                          type="checkbox"
                          checked={order.IsChecked || false}
                          onChange={() => handleToggleOrder(order.Id)}
                          className="h-4 w-4"
                        />
                      </td>
                    )
                  : null
              }
            />

            {/* Summary */}
            <div className="mt-4 flex justify-end gap-4">
              <div className="px-4 py-2 bg-gray-100 rounded-lg">
                <span className="text-sm text-gray-700">Selected Count: </span>
                <span className="text-sm font-semibold text-gray-900">{selectedOrderStats.count}</span>
              </div>
              <div className="px-4 py-2 bg-gray-100 rounded-lg">
                <span className="text-sm text-gray-700">Amount Paying: </span>
                <span className="text-sm font-semibold text-gray-900">AED {formatCurrency(selectedOrderStats.amount)}</span>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => {
                  setShowOrdersModal(false);
                  setSelectedClient(null);
                  setVoucherSearch('');
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default CreatePayment;

